// Label — thin wrapper over Radix Label with an optional `required` marker.
//
// When `required` is true, appends a visible `*` in the accent color.
// Consumers should still pass `aria-required` on the associated input;
// the asterisk is purely visual.

import * as LabelPrimitive from '@radix-ui/react-label';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from './utils/cn.js';

export interface LabelProps extends ComponentPropsWithoutRef<typeof LabelPrimitive.Root> {
  required?: boolean;
}

export const Label = forwardRef<ElementRef<typeof LabelPrimitive.Root>, LabelProps>(function Label(
  { className, required, children, ...props },
  ref,
) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn(
        'text-sm font-medium leading-none text-foreground',
        'peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        className,
      )}
      {...props}
    >
      {children}
      {required ? (
        <span aria-hidden="true" className="ml-0.5 text-[color:var(--color-accent-600)]">
          *
        </span>
      ) : null}
    </LabelPrimitive.Root>
  );
});
